/**
 * VirtualFileCreatedAnnotation
 *
 * Renders a visible "Created file" card when the message annotation contains
 * VirtualFilesystem write/patch tool results, so the file appears clickable
 * without expanding the tool activity log.
 */

'use client'

import React from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { FileIcon } from '@/components/ui/file-icon'
import { MINI_IDE_WORKSPACE_EVENT } from '@/lib/chat/mini-ide-workspace'

const VIRTUAL_FILE_KEYS = [
  'VirtualFilesystem__losi__fs_write_file',
  'VirtualFilesystem__losi__fs_patch_file',
  'VirtualFilesystem__losi__fs_list_files'
]

type VirtualFileMeta = {
  id: string
  path: string
  name: string
  updatedAt?: string | null
  mimeType?: string
}

function parseObjectToFiles(obj: Record<string, unknown>): VirtualFileMeta[] {
  const files: VirtualFileMeta[] = []

  if (typeof obj.id === 'string' && typeof obj.path === 'string') {
    const updatedAt =
      (obj.updatedAt as string | null | undefined) ??
      (obj.updated_at as string | null | undefined) ??
      null
    const mimeType =
      (obj.mimeType as string | undefined) ??
      (obj.mime_type as string | undefined)

    files.push({
      id: obj.id as string,
      path: obj.path as string,
      name:
        (obj.name as string) ??
        (obj.path as string).split('/').pop() ??
        (obj.path as string),
      updatedAt,
      mimeType
    })
  }

  if (Array.isArray(obj.files)) {
    for (const raw of obj.files as Array<Record<string, unknown>>) {
      if (!raw || typeof raw !== 'object') continue
      if (typeof raw.id !== 'string' || typeof raw.path !== 'string') continue
      const updatedAt =
        (raw.updatedAt as string | null | undefined) ??
        (raw.updated_at as string | null | undefined) ??
        null
      const mimeType =
        (raw.mimeType as string | undefined) ??
        (raw.mime_type as string | undefined)
      files.push({
        id: raw.id as string,
        path: raw.path as string,
        name:
          (raw.name as string) ??
          (raw.path as string).split('/').pop() ??
          (raw.path as string),
        updatedAt,
        mimeType
      })
    }
  }

  return files
}

function parseResultToFiles(val: unknown): VirtualFileMeta[] {
  if (val == null) return []

  const raw =
    typeof val === 'string'
      ? (() => {
          try {
            return JSON.parse(val) as unknown
          } catch {
            return null
          }
        })()
      : val

  if (!raw || typeof raw !== 'object') return []

  if (Array.isArray(raw)) {
    const out: VirtualFileMeta[] = []
    for (const item of raw) {
      if (!item || typeof item !== 'object') continue
      out.push(...parseObjectToFiles(item as Record<string, unknown>))
    }
    return out
  }

  return parseObjectToFiles(raw as Record<string, unknown>)
}

export interface VirtualFileCreatedAnnotationProps {
  annotation: Record<string, unknown>
  className?: string
}

export function VirtualFileCreatedAnnotation({ annotation, className }: VirtualFileCreatedAnnotationProps) {
  const { t } = useTranslation()

  const files = React.useMemo(() => {
    const out: VirtualFileMeta[] = []
    const seenPaths = new Set<string>()
    for (const key of VIRTUAL_FILE_KEYS) {
      const raw = annotation[key]
      const entry = raw && typeof raw === 'object' && 'result' in raw
        ? (raw as { result?: unknown }).result
        : raw
      const parsedFiles = parseResultToFiles(entry)
      for (const file of parsedFiles) {
        if (seenPaths.has(file.path)) continue
        seenPaths.add(file.path)
        out.push(file)
      }
    }
    return out
  }, [annotation])

  if (files.length === 0) return null

  const openInCanvas = (file: { id: string; path: string; name: string }) => {
    window.dispatchEvent(
      new CustomEvent('openVirtualFileInCanvas', {
        cancelable: true,
        detail: { id: file.id, path: file.path, name: file.name }
      })
    )
  }

  const openWorkspaceInCanvas = () => {
    window.dispatchEvent(
      new CustomEvent(MINI_IDE_WORKSPACE_EVENT, {
        cancelable: true,
        detail: {
          id: `virtual-files:${files.map((file) => file.id).join(':')}`,
          title: 'Files',
          source: 'virtual-files',
          status: 'completed',
          files
        }
      })
    )
  }

  const getIconType = (file: VirtualFileMeta): string => {
    if (file.mimeType) return file.mimeType
    const lower = file.path.toLowerCase()
    if (lower.endsWith('.md')) return 'markdown'
    if (lower.endsWith('.json')) return 'json'
    if (lower.endsWith('.csv')) return 'csv'
    if (lower.endsWith('.pdf')) return 'pdf'
    if (lower.endsWith('.html') || lower.endsWith('.htm')) return 'html'
    if (/\.(png|jpg|jpeg|webp|gif|svg)$/.test(lower)) return 'image'
    return 'plain'
  }

  const getDepth = (file: VirtualFileMeta): number => {
    const segments = file.path.split('/').filter(Boolean)
    return Math.max(0, segments.length - 1)
  }

  const formatDate = (value?: string | null): string | null => {
    if (!value) return null
    try {
      return new Date(value).toLocaleDateString()
    } catch {
      return null
    }
  }

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex items-center justify-between gap-2 rounded-lg border border-border/50 bg-muted/20 px-3 py-2">
        <div className="min-w-0 text-xs text-muted-foreground">
          {files.length} {files.length === 1 ? t('file') : t('files')}
        </div>
        <button
          type="button"
          onClick={openWorkspaceInCanvas}
          className="text-primary shrink-0 text-sm font-medium hover:underline"
        >
          {t('Open workspace')}
        </button>
      </div>
      {files.map((file) => (
        <button
          key={file.id}
          type="button"
          onClick={() => openInCanvas(file)}
          className="border-border/50 bg-muted/30 hover:bg-muted/50 focus-visible:ring-ring flex w-full cursor-pointer items-center gap-3 rounded-lg border p-3 text-left transition-colors focus:outline-none focus-visible:ring-2"
          style={{ paddingLeft: `${8 + getDepth(file) * 12}px` }}
        >
          <FileIcon type={getIconType(file)} size={18} stroke={1.6} />
          <div className="min-w-0 flex-1">
            <div className="font-mono text-sm font-medium" title={file.path}>
              {file.name}
            </div>
            {(() => {
              const isMarkdown =
                file.path.toLowerCase().endsWith('.md') ||
                file.name.toLowerCase().endsWith('.md') ||
                (file.mimeType ?? '').includes('markdown')
              const formattedDate = formatDate(file.updatedAt)
              const subtitle = isMarkdown && formattedDate ? formattedDate : file.path
              return (
                <div className="text-muted-foreground truncate text-xs">
                  {subtitle}
                </div>
              )
            })()}
          </div>
          <span className="text-primary shrink-0 text-sm font-medium">
            {t('Open')}
          </span>
        </button>
      ))}
    </div>
  )
}
